import { HStack, SimpleGrid, Text, useToast } from "@chakra-ui/react";
import { useEffect, useState } from "react";

import { ProductType } from "@bcasmart/doxecure-commons/doxecureTypes";
import { $count, $ok } from "foundation-ts/commons";
import { Nullable, UUID } from "foundation-ts/types";

import { WaitingModal } from "@/components/Modals/WaitingModal";
import { SeaShellBox } from "@/components/SeaShellBox";
import { OrdersCard } from "@/components/SubcriptionCards/OrdersCard";
import { MandateCard }  from "@components/SubcriptionCards/MandateCard";
import { PlanCard }     from "@components/SubcriptionCards/PlanCard";
import { CollaboratorsCard }    from "@/components/SubcriptionCards/CollaboratorsCard";
import { Mandate } from "@components/SubcriptionCards/types";
import { config } from "@/config/config";
import { useMollie } from "@/contexts/MollieContext";
import { useAuthContext } from "@/hooks/useAuthContext";
import { useOrders } from "@/hooks/useOrders";
import { usePaymentStatus } from "@/hooks/usePaymentStatus";
import useProductList from "@/hooks/useProductList";
import { useSubscription } from "@hooks/useSubscription";
import { useUsers } from "@/hooks/useUsers";
import { createCBMandate, updateMandate } from "@services/users.service";
import { defaultErrorToast, defaultSuccessToast } from "@/utils/toast";
import { G1Icon } from "@/utils/icons";
import { OnlineSubscriptionDto } from "@bcasmart/doxecure-commons/doxecureClientTypes";

export const SubscriptionTab = () => {
    const { connectedUser } = useAuthContext();
    const [isPendingPayment, setIsPendingPayment] = useState(false);
    const [pendingMessage, setPendingMessage] = useState<Nullable<string>>(null);
    const [mandate, setMandate] = useState<Nullable<Mandate>>(connectedUser?.corporations?.first()?.mandate as Mandate);
    const { data: subscription, buyCollaborators, buyExtraPack, cancelDowngradingRequest, cancelReducingRequest, cancelTerminationRequest, refreshSubscription, requestCancelSubscription, requestDowngradRenewalToStandardPlan, requestReduceCollaborators, startSubscriptionImmediately, upgradeToUnlimited } = useSubscription(isPendingPayment);
    const { data: activeUsers } = useUsers();
    const { data: orders, downloadInvoice, refreshOrders } = useOrders();
    const { data: productsList } = useProductList();
    const { mollie, ready } = useMollie();
    const { startPendingPayment, handlePaymentError } = usePaymentStatus(subscription, 
        (msg: string) => {
            setPendingMessage(msg);
            setIsPendingPayment(true);
        },
        () => {
            setPendingMessage(null);
            setIsPendingPayment(false);
        }
    );
    const toast = useToast();

    const activeSubscription: Nullable<OnlineSubscriptionDto> = !!subscription?.offline ? null : subscription as OnlineSubscriptionDto;
    const corporation           = connectedUser?.corporations?.first();
    const isTrialPeriod         = $ok(corporation?.trialPeriod?.end);
    const pendingOrder          = orders?.find(o => o.payment?.status === "pending");
    const maxUsers              = $ok(corporation!.trialPeriod?.extraUsers) ? corporation!.trialPeriod!.extraUsers!+1 : activeSubscription?.maxUsers ?? -1;
    const productPack           = productsList?.packs.find(p => p.type === ProductType.Pack);
    const productCollaborator   = productsList?.packs.find(p => p.type === ProductType.Collaborator);
    const productUnlimited      = productsList?.subscriptions.find(p => p.type === ProductType.Subscription && !!p.isUnlimited);
    const productStandard       = productsList?.subscriptions.find(p => p.type === ProductType.Subscription && !p.isUnlimited);
    const prorataCollabPrice    = $ok(activeSubscription?.infosToUpgrade?.collaboratorPrice) ? parseFloat(activeSubscription!.infosToUpgrade!.collaboratorPrice) : (productCollaborator?.price ?? 0);

    const handleCardSubmit = async (cbToken: string) => {
        const checkoutURL = await createCBMandate(corporation!.apid, cbToken);
        if ($ok(checkoutURL)) {
            window.location.href = checkoutURL!;
        }
    };

    const handleMandateSubmit = async (consumerAccount: string, consumerName: string) => {
        const corpDTO = await updateMandate(corporation!.apid, consumerName, consumerAccount);
        if ($ok(corpDTO)) { 
            setMandate(corpDTO.mandate);
            toast(defaultSuccessToast("Nous avons pris en compte votre nouveau mandat SEPA"));
        }
    };

     const handleBuyExtractPack = async () => {
        buyExtraPack.mutate(undefined, {
            onSuccess: () => {
                if ($ok(corporation?.mandate?.cardNumber)) {
                    startPendingPayment("buyPack");
                } else {
                    toast(defaultSuccessToast("Nous avons bien pris en compte votre demande d'achat d'un pack de signatures, celle-ci sera traitée dans les meilleurs délais"));
                }
            },
            onError: () => { handlePaymentError("buyPack"); }
        });
    };

    const handleCancelDowngradingRequest = async () => {
        cancelDowngradingRequest.mutate(undefined, {
            onSuccess: () => { toast(defaultSuccessToast("L'annulation de la demande de retour à l'abonnement standard a bien été prise en compte")); },
            onError: () => { toast(defaultErrorToast("Une erreur est survenue lors de la demande d'annulation du retour à l'abonnement standard")); }
        });
    };

    const handleCancelTerminationRequest = async() => {
        cancelTerminationRequest.mutate(undefined, {
            onSuccess: () => { toast(defaultSuccessToast("L'annulation de la résiliation a bien été prise en compte")); },
            onError: () => { toast(defaultErrorToast("Une erreur est survenue lors de la demande d'annulation de la résiliation")); }
        });
    };

   const handleChangeToUnlimitedPlan = async () => {
        upgradeToUnlimited.mutate(undefined, {
            onSuccess: () => {
                if ($ok(corporation?.mandate?.cardNumber)) {
                    startPendingPayment("upgradeToUnlimited");
                } else {
                    toast(defaultSuccessToast("Nous avons bien pris en compte votre demande de passer votre abonnement à l'illimité, celle-ci sera traitée dans les meilleurs délais"));
                }
            },
            onError: () => { handlePaymentError("upgradeToUnlimited"); }
        });
    };

    const handleDowngradeRenewalToStandardPlan = async () => {
        requestDowngradRenewalToStandardPlan.mutate(undefined, {
            onSuccess: () => { toast(defaultSuccessToast("La demande pour revenir à l'abonnement standard a bien été prise en compte")); },
            onError: () => { toast(defaultErrorToast("Une erreur est survenue lors de la demande pour revenir à l'abonnement stantard")); }
        });
    };

    const handleDownloadInvoice = async (orderId: UUID, invoiceReference: string) => {
        downloadInvoice.mutate({orderId, invoiceReference}, {
            onError: () => { toast(defaultErrorToast(`Une erreur est survenue lors de la récupération de la facture ${invoiceReference}`)); }
        });
    };

    const handleStartNow = async (unlimited: boolean) => {
        startSubscriptionImmediately.mutate(unlimited, {
            onSuccess: () => {
                if ($ok(corporation?.mandate?.cardNumber)) {
                    startPendingPayment("startSubscription");
                } else {
                    toast(defaultSuccessToast("Nous avons bien pris en compte votre demande de démarrer au plus tôt votre abonnement, celle-ci sera traitée dans les meilleurs délais"));
                }
            },
            onError: () => { handlePaymentError("startSubscription"); }
        });
    }

    const handleTerminate = async () => {
        requestCancelSubscription.mutate(undefined, {
            onSuccess: () => { toast(defaultSuccessToast("La demande de résiliation a bien été prise en compte")); },
            onError: () => { toast(defaultErrorToast("Une erreur est survenue lors de la résiliation de votre abonnement")); }
        });
    }

    const handleAddUsers = (count: number) => {
        buyCollaborators.mutate(count, {
            onSuccess: () => {
                if ($ok(corporation?.mandate?.cardNumber)) {
                    startPendingPayment("buyCollaborators");
                } else {
                    toast(defaultSuccessToast(`Nous avons bien pris en compte votre demande d'ajout de ${count} collaborateur${count > 1 ? 's' : ''}, celle-ci sera traitée dans les meilleurs délais`));
                }
            },
            onError: () => { handlePaymentError("buyCollaborators"); }
        });
    }

    const handleReduceUsers = (count: number) => {
        requestReduceCollaborators.mutate(count, {
            onSuccess: () => { toast(defaultSuccessToast("La demande de réduction du nombre de collaborateurs a bien été prise en compte")); },
            onError: () => { toast(defaultErrorToast("Une erreur est survenue lors de la demande de réduction du nombre de collaborateurs")); }
        });
    }

    const handleCancelReduceRequest = () => {
        cancelReducingRequest.mutate(undefined, {
            onSuccess: () => { toast(defaultSuccessToast("L'annulation de la demande de réduction du nombre de collaborateurs a bien été prise en compte")); },
            onError: () => { toast(defaultErrorToast("Une erreur est survenue lors de la tentative d'annulation de la demande de réduction du nombre de collaborateurs")); }
        })
    }

    useEffect(() => { refreshSubscription(); }, []);

    return (
        <>
            <SeaShellBox component="form" px={4} py={10}>
                { $ok(pendingOrder) && (
                    <HStack bg="orange.50" borderRadius="12px" p={4} border="1px solid" borderColor="orange.200" alignItems="center">
                        <G1Icon.Info color="orange" size="48px" />
                        <Text fontSize="sm" color="orange.700">
                            {connectedUser?.apid === pendingOrder?.requesterInfos?.requesterId ? "Votre demande d'achat" : `La demande effectuée par ${pendingOrder?.requesterInfos?.requesterName}`} a été prise en compte. Un email de confirmation vous sera envoyé dès réception de la validation du réglement.
                        </Text> 
                    </HStack>
                )}
                <SimpleGrid columns={{ base: 1, lg: 2}} spacing={5} mb={5}>
                    <PlanCard
                        connectedUser={connectedUser}
                        subscription={activeSubscription}
                        extraUsers={$count(activeUsers) - 1}
                        productUnlimited={productUnlimited}
                        productCollab={productCollaborator}
                        productPack={productPack}
                        productStandard={productStandard}
                        isLoadingStartingNow={startSubscriptionImmediately.isLoading}
                        isLoadingBuyExtraPack={buyExtraPack.isLoading}
                        isLoadingUpgradeToUnlimited={upgradeToUnlimited.isLoading}
                        hasPendingOrder={$ok(pendingOrder)}
                        onBuyExtraPack={handleBuyExtractPack}
                        onCancelDowngradingRequest={handleCancelDowngradingRequest}
                        onCancelTerminationRequest={handleCancelTerminationRequest}
                        onChangeToUnlimitedPlan={handleChangeToUnlimitedPlan}
                        onDowngradeRenewalToStandardPlan={handleDowngradeRenewalToStandardPlan}
                        onStartNow={handleStartNow}
                        onTerminate={handleTerminate}
                    />
                    <CollaboratorsCard
                        activeUsers={$count(activeUsers)}
                        maxUsers={maxUsers}
                        reduceUsers={activeSubscription?.requestReduceUsers?.reduceCount ?? 0}
                        hasPendingOrder={$ok(pendingOrder)}
                        requesterNameToReduce={activeSubscription?.requestReduceUsers?.requesterId === connectedUser?.apid ? "vous-même" : activeSubscription?.requestReduceUsers?.requesterName}
                        isCancelationRequested={$ok(activeSubscription?.requestCancelSubscription)}
                        isTrialPeriod={isTrialPeriod}
                        pricePerExtraUser={productCollaborator?.price ?? 0}
                        prorataPricePerUser={prorataCollabPrice}
                        onAddUsers={handleAddUsers}
                        onCancelReduceRequest={handleCancelReduceRequest}
                        onReduceUsers={handleReduceUsers}
                    />
                    <MandateCard
                        mandate={mandate}
                        isCancelationRequested={$ok(activeSubscription?.requestCancelSubscription)}
                        companyName={config.app.title}
                        testMode={config.app.mollie.testmode}
                        mollie={mollie}
                        mollieReady={ready}
                        onCardSubmit={handleCardSubmit}
                        onMandateSubmit={handleMandateSubmit}
                    />
                </SimpleGrid>

                <OrdersCard orders={orders} onRefreshOrders={refreshOrders} onDownloadInvoice={handleDownloadInvoice} />
            </SeaShellBox>
            <WaitingModal isOpen={isPendingPayment} message={pendingMessage} onClose={() => setIsPendingPayment(false) } />
        </>
    );
};